import React from 'react';
import { Loader2, FileText, Upload, Trash2, ExternalLink, ShieldCheck, Info } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { useConfirm } from '../ui/ConfirmProvider';

/**
 * Dossiers › Réglages › Signature en ligne (lot S0, 2026-09-22 ; déplacé le 2026-10-28) — ce qu'un devis ou un contrat signé en ligne
 * doit porter pour protéger réellement l'établissement :
 *   - les CGV (PDF) que le client accepte par une case NON précochée au moment de signer ;
 *   - le délai de rétractation : 14 jours minimum pour un particulier (contrat à distance, Code de la
 *     consommation L221-18), verrouillé côté serveur ; pour un professionnel la loi n'en impose aucun
 *     à distance, l'établissement peut en accorder un volontairement (0 = aucun).
 * Les mentions « Bon pour accord » / « Devis reçu avant l'exécution des prestations » sont fixes : elles
 * sont imprimées telles quelles sur la page de signature (lot S1).
 */
// Formules « moyen:quand » proposées à l'établissement, avec leur libellé (functions/api/modules/paiements.js).
const FORMULES_PAIEMENT: Record<'professionnel', [string, string][]> = {
    professionnel: [
        ['carte:apres_prestation', 'Carte bancaire (lien de paiement) : après chaque prestation'],
        ['carte:une_fois', 'Carte bancaire : en une seule fois (contrat annuel)'],
        ['sepa:apres_prestation', 'Prélèvement SEPA : après chaque prestation'],
        ['sepa:mensuel_12', 'Prélèvement SEPA : mensualisé sur 12 mois (contrat annuel)'],
        ['sepa:mensuel_24', 'Prélèvement SEPA : mensualisé sur 24 mois (contrat annuel)'],
    ],
};

export const SignatureSettings: React.FC = () => {
    const confirmAction = useConfirm();
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [cgv, setCgv] = React.useState<{ file_name: string; file_size: number; updated_at: string } | null>(null);
    const [cgvBusy, setCgvBusy] = React.useState(false);
    const [joursParticulier, setJoursParticulier] = React.useState('14');
    const [joursPro, setJoursPro] = React.useState('0');
    const [formeJuridique, setFormeJuridique] = React.useState('');
    const [mediateur, setMediateur] = React.useState('');
    // Moyens de paiement proposés après la signature (2026-10-22) : formules « moyen:quand » activées par public.
    const [paiements, setPaiements] = React.useState<{ professionnel: string[] }>({ professionnel: [] });

    const charger = React.useCallback(() => {
        moduleApi.getBillingSettings().then((r: any) => {
            setCgv(r?.cgv_document_id ? { file_name: r.cgv_file_name || 'cgv.pdf', file_size: Number(r.cgv_file_size) || 0, updated_at: r.cgv_updated_at || '' } : null);
            setJoursParticulier(String(r?.retractation_days_particulier ?? 14));
            setJoursPro(String(r?.retractation_days_pro ?? 0));
            setFormeJuridique(r?.legal_form || '');
            setMediateur(r?.cgv_mediator || '');
            if (r?.payment_methods) setPaiements({ professionnel: r.payment_methods.professionnel || [] });
        }).catch((e: any) => setErreur(e?.message || 'Erreur de chargement')).finally(() => setLoading(false));
    }, []);
    React.useEffect(() => { charger(); }, [charger]);

    const deposerCgv = async (file: File) => {
        setCgvBusy(true); setErreur('');
        try {
            const data: string = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
                reader.readAsDataURL(file);
            });
            const res: any = await moduleApi.uploadCgv({ document_base64: data, file_name: file.name });
            if (res?.error) { setErreur(res.error); return; }
            setCgv({ file_name: res.cgv_file_name, file_size: res.cgv_file_size, updated_at: res.cgv_updated_at });
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors du dépôt des CGV.');
        } finally { setCgvBusy(false); }
    };

    const retirerCgv = async () => {
        const ok = await confirmAction({
            title: 'Retirer les CGV ?',
            description: 'Sans CGV déposées, la case « J’accepte les conditions générales de vente » ne pourra plus être proposée à la signature.',
            confirmLabel: 'Retirer les CGV',
            destructive: true,
        });
        if (!ok) return;
        setCgvBusy(true); setErreur('');
        try {
            const res: any = await moduleApi.deleteCgv();
            if (res?.error) { setErreur(res.error); return; }
            setCgv(null);
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors du retrait des CGV.');
        } finally { setCgvBusy(false); }
    };

    // La lecture est authentifiée (jeton en en-tête) : un simple lien ne le porterait pas.
    const ouvrirCgv = async () => {
        try {
            const token = localStorage.getItem('token');
            const r = await fetch('/api/legal-documents/cgv', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!r.ok) { setErreur("Impossible d'ouvrir les CGV."); return; }
            window.open(URL.createObjectURL(await r.blob()), '_blank', 'noopener');
        } catch { setErreur("Impossible d'ouvrir les CGV."); }
    };

    const enregistrer = async () => {
        setSaving(true); setErreur(''); setSaved(false);
        try {
            const res: any = await moduleApi.saveBillingSettings({
                retractation_days_particulier: Number(joursParticulier),
                retractation_days_pro: Number(joursPro),
                legal_form: formeJuridique,
                cgv_mediator: mediateur,
                payment_methods: paiements,
            });
            if (res?.error) { setErreur(res.error); return; }
            setSaved(true); setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement.");
        } finally { setSaving(false); }
    };

    if (loading) return <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-content-muted" /></div>;

    const champ = 'w-24 bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 text-right';
    const fmtTaille = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
    const fmtDate = (d: string) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; } };
    const basculerPaiement = (formule: string) => {
        setPaiements((p) => {
            if (p.professionnel.includes(formule)) return p.professionnel.length <= 1 ? p : { professionnel: p.professionnel.filter((f) => f !== formule) };
            return { professionnel: [...p.professionnel, formule] };
        });
    };
    const particulierInvalide = !Number.isFinite(Number(joursParticulier)) || Number(joursParticulier) < 14;

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-base font-bold text-content flex items-center gap-2"><ShieldCheck size={18} /> Signature en ligne</h2>
                <p className="text-xs text-content-muted mt-1">Ce que vos devis et contrats signés en ligne doivent porter pour vous protéger réellement : vos conditions générales de vente, et le délai de rétractation de vos clients.</p>
            </div>

            <section className="bg-surface border border-border rounded-2xl p-5 space-y-3">
                <div>
                    <h3 className="text-sm font-bold text-content">Conditions générales de vente (CGV)</h3>
                    <p className="text-xs text-content-muted mt-0.5">Au moment de signer, le client doit cocher lui-même « J’accepte les conditions générales de vente » et peut les lire d’un clic. Sans PDF déposé, l’application propose automatiquement ses propres CGV (une version particuliers, une version professionnels), remplies avec vos informations ci-dessous et vos paramètres de facturation. Un PDF déposé les remplace.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-content">Forme juridique</label>
                        <input type="text" value={formeJuridique} onChange={(e) => setFormeJuridique(e.target.value)} placeholder="ex : SASU, EURL, entreprise individuelle" className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-content">Médiateur de la consommation</label>
                        <input type="text" value={mediateur} onChange={(e) => setMediateur(e.target.value)} placeholder="Nom, adresse et site du médiateur" className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    </div>
                </div>
                <p className="text-[11px] text-content-muted">Le médiateur est obligatoire pour vendre à des particuliers. Ces deux champs, enregistrés avec le bouton « Enregistrer » plus bas, complètent les CGV générées.</p>
                {cgv ? (
                    <div className="flex items-center gap-3 flex-wrap p-3 rounded-xl bg-surface-soft border border-border">
                        <FileText size={18} className="text-brand-600 dark:text-brand-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-content truncate">{cgv.file_name}</p>
                            <p className="text-[11px] text-content-muted">{fmtTaille(cgv.file_size)}{cgv.updated_at ? ` · déposées le ${fmtDate(cgv.updated_at)}` : ''}</p>
                        </div>
                        <button type="button" onClick={ouvrirCgv} className="inline-flex items-center gap-1 text-xs font-bold text-content-muted hover:text-content"><ExternalLink size={13} /> Ouvrir</button>
                        <label className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-surface border border-border text-content hover:border-brand-500 cursor-pointer ${cgvBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                            {cgvBusy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Remplacer
                            <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) deposerCgv(f); e.target.value = ''; }} />
                        </label>
                        <button type="button" onClick={retirerCgv} disabled={cgvBusy} title="Retirer les CGV" className="text-content-muted hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                    </div>
                ) : (
                    <label className={`block cursor-pointer ${cgvBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                        <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) deposerCgv(f); e.target.value = ''; }} />
                        <div className="border-2 border-dashed border-border hover:border-brand-500/50 rounded-xl p-5 flex flex-col items-center justify-center gap-1 transition-colors">
                            {cgvBusy ? <Loader2 size={22} className="animate-spin text-content-muted" /> : <Upload size={22} className="text-content-muted" />}
                            <p className="text-xs font-bold text-content">Déposer vos CGV (PDF)</p>
                            <p className="text-[10px] text-content-muted">10 Mo maximum — une seule version à la fois, un nouveau dépôt remplace l’ancien</p>
                        </div>
                    </label>
                )}
            </section>

            <section className="bg-surface border border-border rounded-2xl p-5 space-y-4">
                <div>
                    <h3 className="text-sm font-bold text-content">Délai de rétractation</h3>
                    <p className="text-xs text-content-muted mt-0.5">Un devis ou un contrat signé à distance ouvre un droit de rétractation. La page de signature l’affiche au client, avec le formulaire de rétractation.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-content">Particuliers</label>
                        <div className="flex items-center gap-2">
                            <input type="number" min={14} max={365} value={joursParticulier} onChange={(e) => setJoursParticulier(e.target.value)} className={`${champ} ${particulierInvalide ? 'border-red-500' : ''}`} />
                            <span className="text-sm text-content-muted">jours</span>
                        </div>
                        <p className="text-[11px] text-content-muted">14 jours minimum, c’est la loi (art. L221-18 du Code de la consommation) — vous pouvez accorder plus, jamais moins. Si la prestation a lieu avant la fin du délai, le client y renonce expressément en signant.</p>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-content">Professionnels</label>
                        <div className="flex items-center gap-2">
                            <input type="number" min={0} max={365} value={joursPro} onChange={(e) => setJoursPro(e.target.value)} className={champ} />
                            <span className="text-sm text-content-muted">jours</span>
                        </div>
                        <p className="text-[11px] text-content-muted">Aucun délai légal pour un contrat à distance entre professionnels. 0 = aucun délai ; vous pouvez en accorder un volontairement.</p>
                    </div>
                </div>
                <div className="flex items-start gap-2 p-3 rounded-xl bg-surface-soft border border-border text-[11px] text-content-muted">
                    <Info size={14} className="shrink-0 mt-0.5" />
                    <span>Les mentions « Bon pour accord » et « Devis reçu avant l’exécution des prestations », la date, le nom du signataire, son adresse email vérifiée par code et l’empreinte du document sont imprimés automatiquement sur chaque devis et contrat signé en ligne.</span>
                </div>
                <div className="flex items-center gap-3">
                    <Button disabled={saving || particulierInvalide} onClick={enregistrer}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : null} {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                    {saved && <span className="text-xs font-bold text-green-500">Enregistré ✓</span>}
                </div>
            </section>

            <section className="bg-surface border border-border rounded-2xl p-5 space-y-4">
                <div>
                    <h3 className="text-sm font-bold text-content">Moyens de paiement proposés après la signature</h3>
                    <p className="text-xs text-content-muted mt-0.5">Particuliers : un lien de paiement Stripe, toujours en une seule fois, sans choix pour le client. Le paiement en plusieurs fois (Klarna…), Apple Pay et Google Pay se règlent directement dans votre compte Stripe. Professionnels : votre client choisit parmi ce que vous cochez ci-dessous.</p>
                </div>
                <fieldset className="space-y-2">
                    <legend className="text-xs font-bold text-content mb-1">Professionnels</legend>
                    {FORMULES_PAIEMENT.professionnel.map(([formule, libelle]) => (
                        <label key={formule} className="flex items-start gap-2.5 text-sm text-content cursor-pointer">
                            <input type="checkbox" checked={paiements.professionnel.includes(formule)} onChange={() => basculerPaiement(formule)}
                                className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600" />
                            <span>{libelle}</span>
                        </label>
                    ))}
                </fieldset>
                <div className="flex items-start gap-2 p-3 rounded-xl bg-surface-soft border border-border text-[11px] text-content-muted">
                    <Info size={14} className="shrink-0 mt-0.5" />
                    <span>Le prélèvement SEPA est réservé aux professionnels et demande que votre compte Stripe soit connecté. Gardez au moins un moyen. Un client démarché à domicile ne reçoit aucune demande de paiement avant le délai légal.</span>
                </div>
                <div className="flex items-center gap-3">
                    <Button disabled={saving || particulierInvalide} onClick={enregistrer}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : null} {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                    {saved && <span className="text-xs font-bold text-green-500">Enregistré ✓</span>}
                </div>
            </section>

            {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}
        </div>
    );
};
